import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editCredits, setEditCredits] = useState(0);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users-detailed');
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error('Error loading users');
    } finally {
      setLoading(false);
    }
  };

  const updateUserCredits = async (userId, newCredits) => {
    try {
      await api.patch(`/admin/users/${userId}/credits`, {
        membershipListings: newCredits
      });
      toast.success('User credits updated successfully');
      loadUsers();
      setShowEditModal(false);
    } catch (error) {
      toast.error('Error updating user credits');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await api.patch(`/admin/users/${userId}/status`, {
        isActive: !currentStatus
      });
      toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      loadUsers();
    } catch (error) {
      toast.error('Error updating user status');
    }
  };

  const deleteUser = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted permanently');
      loadUsers();
      setShowDeleteModal(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting user');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.phone.includes(searchTerm);
    
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'active' && user.isActive !== false) ||
                         (filterStatus === 'inactive' && user.isActive === false) ||
                         (filterStatus === 'premium' && user.membershipListings > 0);
    
    return matchesSearch && matchesFilter;
  });

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditCredits(user.membershipListings || 0);
    setShowEditModal(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-t-4 rounded-full animate-spin mx-auto mb-4" 
               style={{ borderColor: '#DFD0B8', borderTopColor: '#948979' }}></div>
          <p className="font-medium" style={{ color: '#393E46' }}>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="space-y-6 lg:space-y-8">
        {/* Enhanced Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 lg:mb-8">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#948979' }}>
                <i className="fas fa-users text-white text-sm lg:text-base"></i>
              </div>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold truncate" style={{ color: '#222831' }}>
                User Management
              </h2>
            </div>
            <p className="ml-11 lg:ml-13 text-sm lg:text-base" style={{ color: '#393E46' }}>Manage users, credits, and account status</p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center space-x-4">
            <div className="text-white px-3 py-2 lg:px-4 lg:py-2 rounded-2xl shadow-lg text-sm lg:text-base" style={{ backgroundColor: '#948979' }}>
              <span className="font-semibold">{filteredUsers.length}</span>
              <span className="ml-1" style={{ color: '#DFD0B8' }}>Users</span>
            </div>
          </div>
        </div>

        {/* Enhanced Filters */}
        <div className="bg-white rounded-2xl lg:rounded-3xl shadow-xl p-6 lg:p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
          <div className="flex flex-col sm:flex-row gap-4 lg:gap-6">
            <div className="flex-1">
              <label className="block text-sm font-semibold mb-3" style={{ color: '#393E46' }}>Search Users</label>
              <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <i className="fas fa-search" style={{ color: '#948979' }}></i>
              </div>
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 lg:py-4 border-2 rounded-2xl focus:ring-2 focus:border-transparent transition-all duration-200 text-sm lg:text-base"
                style={{ 
                  borderColor: '#DFD0B8', 
                  backgroundColor: '#DFD0B8',
                  color: '#222831'
                }}
              />
            </div>
          </div>
          <div className="sm:w-48">
            <label className="block text-sm font-semibold mb-3" style={{ color: '#393E46' }}>Filter Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 lg:px-6 py-3 lg:py-4 border-2 rounded-2xl focus:ring-2 focus:border-transparent transition-all duration-200 text-sm lg:text-base"
              style={{ 
                borderColor: '#DFD0B8', 
                backgroundColor: '#DFD0B8',
                color: '#222831'
              }}
            >
              <option value="all">All Users</option>
              <option value="active">Active Users</option>
              <option value="inactive">Inactive Users</option>
              <option value="premium">Premium Users</option>
            </select>
          </div>
        </div>
      </div>

      {/* Enhanced Users Grid */}
      <div className="grid gap-4 lg:gap-6">
        {filteredUsers.length === 0 ? (
          <div className="bg-white rounded-2xl lg:rounded-3xl shadow-xl p-12 lg:p-16 text-center border-2" style={{ borderColor: '#DFD0B8' }}>
            <div className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#DFD0B8' }}>
              <i className="fas fa-users text-3xl" style={{ color: '#948979' }}></i>
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ color: '#222831' }}>No users found</h3>
            <p style={{ color: '#393E46' }}>Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <div className="grid gap-4 lg:gap-6">
            {filteredUsers.map((user) => (
              <div key={user._id} className="bg-white rounded-2xl lg:rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 lg:p-8 border-2 overflow-hidden" style={{ borderColor: '#DFD0B8' }}>
                <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 lg:space-x-6">
                  {/* User Avatar */}
                  <div className="flex-shrink-0 self-center sm:self-auto">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center text-white font-bold text-lg lg:text-xl shadow-lg" style={{ backgroundColor: '#948979' }}>
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                  </div>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="space-y-3">
                      <div className="text-center sm:text-left">
                        <h3 className="text-lg lg:text-xl font-bold truncate" style={{ color: '#222831' }}>{user.name}</h3>
                        <p className="text-sm lg:text-base truncate" style={{ color: '#393E46' }}>{user.email}</p>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0 text-xs lg:text-sm" style={{ color: '#393E46' }}>
                        <div className="flex items-center justify-center sm:justify-start space-x-1">
                          <i className="fas fa-phone" style={{ color: '#948979' }}></i>
                          <span className="truncate">{user.phone}</span>
                        </div>
                        <div className="flex items-center justify-center sm:justify-start space-x-1">
                          <i className="fas fa-map-marker-alt" style={{ color: '#948979' }}></i>
                          <span className="truncate">{user.location}</span>
                        </div>
                      </div>

                      <div className="flex flex-wrap justify-center sm:justify-start gap-2">
                        {user.isEmailVerified ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <i className="fas fa-check-circle mr-1"></i>
                            Verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <i className="fas fa-times-circle mr-1"></i>
                            Unverified
                          </span>
                        )}
                        
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          user.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {user.isActive !== false ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Stats Cards */}
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:gap-4 w-full sm:w-auto">
                    <div className="text-center p-2 lg:p-3 rounded-xl lg:rounded-2xl border-2" style={{ backgroundColor: '#DFD0B8', borderColor: '#948979' }}>
                      <div className="text-lg lg:text-2xl font-bold" style={{ color: '#222831' }}>{user.freeListingsUsed || 0}</div>
                      <div className="text-xs font-medium" style={{ color: '#393E46' }}>Free Used</div>
                    </div>
                    <div className="text-center p-2 lg:p-3 rounded-xl lg:rounded-2xl border-2" style={{ backgroundColor: '#DFD0B8', borderColor: '#948979' }}>
                      <div className="text-lg lg:text-2xl font-bold" style={{ color: '#948979' }}>{user.membershipListings || 0}</div>
                      <div className="text-xs font-medium" style={{ color: '#393E46' }}>Premium</div>
                    </div>
                    <div className="text-center p-2 lg:p-3 rounded-xl lg:rounded-2xl border-2" style={{ backgroundColor: '#DFD0B8', borderColor: '#948979' }}>
                      <div className="text-lg lg:text-2xl font-bold" style={{ color: '#222831' }}>₹{user.totalSpent || 0}</div>
                      <div className="text-xs font-medium" style={{ color: '#393E46' }}>Total Spent</div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-center sm:justify-end space-x-2 pt-2 sm:pt-0">
                    <button
                      onClick={() => openEditModal(user)}
                      className="text-white p-2 lg:p-3 rounded-xl lg:rounded-2xl hover:shadow-xl transition-all duration-200 shadow-lg"
                      style={{ backgroundColor: '#948979' }}
                      title="Edit Credits"
                    >
                      <i className="fas fa-edit text-sm lg:text-base"></i>
                    </button>
                    <button
                      onClick={() => toggleUserStatus(user._id, user.isActive !== false)}
                      className={`p-2 lg:p-3 rounded-xl lg:rounded-2xl transition-all duration-200 shadow-lg hover:shadow-xl text-white`}
                      style={{ backgroundColor: user.isActive !== false ? '#dc2626' : '#22c55e' }}
                      title={user.isActive !== false ? 'Deactivate User' : 'Activate User'}
                    >
                      <i className={`fas ${user.isActive !== false ? 'fa-ban' : 'fa-check'} text-sm lg:text-base`}></i>
                    </button>
                    <button
                      onClick={() => openDeleteModal(user)}
                      className="p-2 lg:p-3 rounded-xl lg:rounded-2xl transition-all duration-200 shadow-lg hover:shadow-xl text-white"
                      style={{ backgroundColor: '#dc2626' }}
                      title="Delete User Permanently"
                    >
                      <i className="fas fa-trash text-sm lg:text-base"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Enhanced Edit Credits Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-3xl mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold shadow-lg" style={{ backgroundColor: '#948979' }}>
                {selectedUser.name.charAt(0).toUpperCase()}
              </div>
              <h3 className="text-2xl font-bold mb-2" style={{ color: '#222831' }}>
                Edit Credits
              </h3>
              <p style={{ color: '#393E46' }}>{selectedUser.name}</p>
              <p className="text-sm" style={{ color: '#393E46' }}>{selectedUser.email}</p>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-3" style={{ color: '#393E46' }}>
                  Premium Listings Credits
                </label>
                <input
                  type="number"
                  min="0"
                  value={editCredits}
                  onChange={(e) => setEditCredits(parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-4 border-2 rounded-2xl focus:ring-2 focus:border-transparent transition-all duration-200"
                  style={{ 
                    borderColor: '#DFD0B8', 
                    backgroundColor: '#DFD0B8',
                    color: '#222831'
                  }}
                />
              </div>

              <div className="rounded-2xl p-6 border-2" style={{ backgroundColor: '#DFD0B8', borderColor: '#948979' }}>
                <h4 className="font-semibold mb-4" style={{ color: '#222831' }}>Current Status</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-lg font-bold" style={{ color: '#222831' }}>{selectedUser.freeListingsUsed || 0}/3</div>
                    <div className="text-xs" style={{ color: '#393E46' }}>Free Used</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold" style={{ color: '#948979' }}>{selectedUser.membershipListings || 0}</div>
                    <div className="text-xs" style={{ color: '#393E46' }}>Premium</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold" style={{ color: '#222831' }}>₹{selectedUser.totalSpent || 0}</div>
                    <div className="text-xs" style={{ color: '#393E46' }}>Spent</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex space-x-4 mt-8">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-6 py-4 border-2 rounded-2xl hover:bg-gray-50 transition-colors font-semibold"
                style={{ borderColor: '#DFD0B8', color: '#393E46' }}
              >
                Cancel
              </button>
              <button
                onClick={() => updateUserCredits(selectedUser._id, editCredits)}
                className="flex-1 px-6 py-4 rounded-2xl text-white font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                style={{ backgroundColor: '#948979' }}
              >
                Update Credits
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-3xl mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold shadow-lg" style={{ backgroundColor: '#dc2626' }}>
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <h3 className="text-2xl font-bold mb-2" style={{ color: '#222831' }}>
                Delete User
              </h3>
              <p className="mb-2" style={{ color: '#393E46' }}>
                Are you sure you want to permanently delete this user?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-4">
                <p className="text-sm text-red-800 font-medium">
                  <strong>{selectedUser.name}</strong>
                </p>
                <p className="text-sm text-red-600">{selectedUser.email}</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4">
                <p className="text-sm text-yellow-800">
                  <i className="fas fa-warning mr-2"></i>
                  This action cannot be undone. All user data, listings, and payments will be permanently deleted.
                </p>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-6 py-4 border-2 rounded-2xl hover:bg-gray-50 transition-colors font-semibold"
                style={{ borderColor: '#DFD0B8', color: '#393E46' }}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteUser(selectedUser._id)}
                className="flex-1 px-6 py-4 rounded-2xl text-white font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                style={{ backgroundColor: '#dc2626' }}
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default UserManagement;